import React, { useRef } from "react"

import useTracking from "hooks/useTracking"
import useProductTracking from "hooks/useProductTracking"
import { useComponentizedPage } from "store/contexts/componentized-page"

import ArticleCard from "components/card/article"
import ClassCard from "components/card/class"
import PodcastCard from "components/card/podcast"
import LinkCard from "components/card/link"
import SupplementsCard from "components/card/supplements"
import NavigationCard from "components/card/navigation"
import ContentfulVideo from "./video"

const ContentfulCard = ({
  component,
  tileVariant,
  isGrid,
  fullHeight,
  pageType,
  isScrollable,
  isHomePage,
  renderImage = true,
  isRailItem,
  ...props
}) => {
  const {
    itemType,
    itemId,
    headlineCard: headline,
    subheadline,
    category,
    byline,
    reviewByline,
    url,
    imageCrops,
    image,
    partnerLogo,
    partnershipType,
    trackingId,
    tracking,
    previewVideo,
    id,
    hideOnPageLoad,
    partnerName,
    partnerUrl,
    article,
  } = populateFieldsFromArticle(component)

  const { trackRender, trackView, trackClick } = useTracking()
  const { trackProductClick } = useProductTracking()
  const ref = useRef(null)

  let trackId = trackingId || `${itemType?.toLowerCase()}-${itemId}-card`
  let trackProps = {}

  if (itemType === "Article" && article) {
    const [articleTrackingId, articleTrackingProps] = getArticleTrackingDetails(article, pageType)
    if (articleTrackingId) {
      trackId = articleTrackingId
    }
    trackProps = { ...trackProps, ...articleTrackingProps }
  }

  if (tracking && tracking.includes("Render")) {
    trackRender(ref, trackId, trackProps)
  }

  if (tracking && tracking.includes("View")) {
    trackView(ref, trackId, trackProps)
  }

  if (tracking && tracking.includes("Click")) {
    trackClick(ref, trackId, trackProps)
  }

  const { classesOnPage } = useComponentizedPage()

  if (itemType === "Class") {
    trackProductClick({
      elementRef: ref,
      item: { ...component, cls: classesOnPage.find((c) => `${c.id}` === component.itemId) },
    })
  }

  if (itemType === "Product") {
    trackProductClick({ elementRef: ref, item: component })
  }

  let card

  switch (`${itemType}${tileVariant ? `-${tileVariant}` : ""}`) {
    case "Article":
      card = (
        <ArticleCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          category={category}
          headline={headline}
          {...(!isGrid && {
            subheadline:
              subheadline &&
              subheadline.childMarkdownRemark &&
              subheadline.childMarkdownRemark.html,
          })}
          author={byline}
          reviewedBy={reviewByline}
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          partnerLogoUrl={partnerLogo && partnerLogo.length && partnerLogo[0].secure_url}
          partnerName={partnerName}
          partnerUrl={partnerUrl}
          partnershipType={partnershipType}
          isGrid={isGrid}
          video={
            previewVideo && (
              <ContentfulVideo
                isGrid={isGrid}
                fullHeight={fullHeight}
                component={previewVideo}
                isVideoCard
                nested
              />
            )
          }
          renderImage={renderImage}
          isRailItem={isRailItem}
        />
      )
      break
    case "Article-tileSmall":
    case "Article-tileMedium":
      card = (
        <ArticleCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          category={category}
          headline={headline}
          author={byline}
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          partnerLogoUrl={partnerLogo && partnerLogo.length && partnerLogo[0].secure_url}
          partnerName={partnerName}
          partnerUrl={partnerUrl}
          partnershipType={partnershipType}
          isGrid={isGrid}
          video={
            previewVideo && (
              <ContentfulVideo
                component={previewVideo}
                isVideoCard
                isGrid={isGrid}
                fullHeight={fullHeight}
              />
            )
          }
          renderImage={renderImage}
        />
      )
      break
    case "Article-tileJumbo":
    case "Article-tileLarge":
    case "Article-tileTall":
      card = (
        <ArticleCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          category={category}
          headline={headline}
          subheadline={
            subheadline && subheadline.childMarkdownRemark && subheadline.childMarkdownRemark.html
          }
          author={byline}
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          partnerLogoUrl={partnerLogo && partnerLogo.length && partnerLogo[0].secure_url}
          partnerName={partnerName}
          partnerUrl={partnerUrl}
          partnershipType={partnershipType}
          isGrid={isGrid}
          renderImage={renderImage}
        />
      )
      break
    case "Class":
      card = (
        <ClassCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          author={byline}
          category={category}
          headline={headline}
          {...(!isGrid && {
            subheadline:
              subheadline &&
              subheadline.childMarkdownRemark &&
              subheadline.childMarkdownRemark.html,
          })}
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          isGrid={isGrid}
        />
      )
      break
    case "Product":
      card = (
        <SupplementsCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          category={category}
          headline={headline}
          subheadline={
            subheadline && subheadline.childMarkdownRemark && subheadline.childMarkdownRemark.html
          }
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          isGrid={isGrid}
        />
      )
      break
    case "Podcast":
      card = (
        <PodcastCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          category={previewVideo ? byline : category}
          headline={headline}
          subheadline={
            subheadline && subheadline.childMarkdownRemark && subheadline.childMarkdownRemark.html
          }
          image={image}
          imageCrops={imageCrops}
          video={previewVideo}
          isGrid={isGrid}
          byline={byline}
          reviewByline={reviewByline}
          isScrollable={isScrollable}
        />
      )
      break
    case "Link":
      card = (
        <LinkCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          headline={headline}
          subheadline={
            subheadline && subheadline.childMarkdownRemark && subheadline.childMarkdownRemark.html
          }
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          video={previewVideo}
          isGrid={isGrid}
        />
      )
      break
    case "Navigation":
      card = (
        <NavigationCard
          {...props}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={ref}
          link={url}
          headline={headline}
          imageUrl={image && image.length && image[0].secure_url}
          imageCrops={imageCrops}
          isGrid={isGrid}
          {...(props?.closeButtonRef &&
            props?.closeButtonRef.current && { closeButtonRef: props.closeButtonRef })}
        />
      )
      break
    default:
      card = <div />
  }

  return card
}

const populateFieldsFromArticle = (item) => {
  const { itemType, article } = item

  if (itemType === "Article" && article) {
    item.partnershipType =
      item.partnershipType || article.partner ? "Partner" : article.sponsor ? "Sponsor" : null
    item.headlineCard = item.headlineCard || article.titles?.site
    item.category = item.category || article.subcategory || article.category
    item.byline = item.byline || (article.authors ? article.authors[0]?.name : "")
    item.url = item.url || `/articles/${article.url}`
    item.imageCrops =
      item.imageCrops || !article.previewImage ? item.imageCrops : JSON.parse(article.previewImage)
    item.subheadline = item.subheadline || article.previewText
    item.trackingId = item.trackingId || `article-${article.id}`
    item.reviewByline = item.reviewByline || (article.reviewers ? article.reviewers[0].name : null)

    if (item.partnershipType) {
      const partner = item.partnershipType === "Sponsor" ? "sponsor" : "partner"
      item.partnerLogo = item.partnerLogo || article[partner]?.previewLogo || article[partner]?.logo
      item.partnerName = article[partner]?.name || null
      item.partnerUrl = `/partner/${article[partner]?.url}` || null
    }
  }

  return item
}

const getArticleTrackingDetails = (article, pageType) => {
  const { id, partner, sponsor, titles, url, category, subcategory } = article

  const collaborator = partner || sponsor
  const trackingId = partner ? "partner_article" : sponsor ? "sponsor_article" : null

  const trackingProps = {
    ...(pageType && { page_type: pageType }),
    click_title: titles.site || titles.body,
    click_url: `/articles/${url}`,
    article_id: id,
    ...(collaborator &&
      (partner ? { partner_id: collaborator.partnerId } : { sponsor_id: collaborator.sponsorId })),
    category: "Content Engagement",
    content_category: category,
    content_subcategory: subcategory,
  }

  return [trackingId, trackingProps]
}

export default ContentfulCard
